import type {
  ColumnGroup,
  TableColumn,
} from '@data/components/basic/Table/types';
import type { MetricsTableColumn } from '@data/hooks/api/use-metrics-table-v2-api/use-metrics-table-v2-api';

/**
 * Flattens API columns that may contain `type: "group"` entries with nested
 * `columns` arrays into a flat list of leaf columns suitable for the Table
 * component, plus an array of ColumnGroup descriptors for the grouped header
 * row.
 *
 * Non-group columns (e.g. id, string, user) are passed through as-is.
 * Group columns are expanded into their children.
 */
export function flattenGroupedColumns(apiColumns: MetricsTableColumn[]): {
  columns: TableColumn[];
  columnGroups: ColumnGroup[];
} {
  const hasGroups = apiColumns.some(
    (col) => col.type === 'group' && 'columns' in col
  );

  const columns: TableColumn[] = apiColumns.flatMap((col) => {
    if (col.type === 'group' && 'columns' in col && col.columns) {
      return col.columns as TableColumn[];
    }
    return [col as TableColumn];
  });

  const columnGroups: ColumnGroup[] = hasGroups
    ? apiColumns
        .filter((col) => col.type !== 'id')
        .map((col) => {
          if (col.type === 'group' && 'columns' in col && col.columns) {
            return { label: col.label, colSpan: col.columns.length };
          }
          return { label: '', colSpan: 1 };
        })
    : [];

  return { columns, columnGroups };
}
